import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CheckCircleIcon, XCircleIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/solid'
import { DownloadIcon } from '@heroicons/react/outline'
import { buildCycleCountCsv } from './buildCycleCountCsv'
import { downloadCsv } from '../../utils/csvUtils'
import { ButtonLoader } from '../../components'
import { UnassignCycleCountDialog } from './UnassignCycleCountDialog'
import { ReassignCycleCountDialog } from './ReassignCycleCountDialog'
import { foundStockDeepLink } from '../FoundStock/foundStockLinks'
import { locationHasSurplus } from './foundStockLinks'

const isActionableStatus = status => status === 'pending' || status === 'review_pending' || status === 'submitted'

const T_PREFIX = 'cycle_counts.task_panel'

const LOCATION_STATUS_CONFIG = {
  pending: { color: 'bg-gray-100 text-gray-800', key: 'status_pending' },
  submitted: { color: 'bg-yellow-100 text-yellow-800', key: 'status_submitted' },
  review_pending: { color: 'bg-orange-100 text-orange-800', key: 'status_review_pending' },
  review_submitted: { color: 'bg-blue-100 text-blue-800', key: 'status_review_submitted' },
  resolved: { color: 'bg-green-100 text-green-800', key: 'status_resolved' },
}

const OUTCOME_CONFIG = {
  confirmed: { color: 'bg-green-100 text-green-800', key: 'outcome_confirmed' },
  counter_confirmed: { color: 'bg-yellow-100 text-yellow-800', key: 'outcome_counter_confirmed' },
  disagreement: { color: 'bg-red-100 text-red-800', key: 'outcome_disagreement' },
}

const ResultsTable = ({ results, i18n, countedLabel }) => (
  <table className="min-w-full divide-y divide-gray-200">
    <thead className="bg-gray-50">
      <tr>
        <th className="px-3 py-1 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t(`${T_PREFIX}.product`)}</th>
        <th className="px-3 py-1 text-center text-xs font-medium text-gray-500 uppercase">{countedLabel}</th>
        <th className="px-3 py-1 text-center text-xs font-medium text-gray-500 uppercase">{i18n.t(`${T_PREFIX}.system`)}</th>
        <th className="px-3 py-1 text-center text-xs font-medium text-gray-500 uppercase">{i18n.t(`${T_PREFIX}.result`)}</th>
      </tr>
    </thead>
    <tbody className="bg-white divide-y divide-gray-200">
      {results.map((item, index) => (
        <tr key={index} className={!item.match ? 'bg-red-50' : ''}>
          <td className="px-3 py-1 text-sm text-gray-900">{item.product_name || item.product_sku || `Product #${item.product_id}`}</td>
          <td className="px-3 py-1 text-sm text-center font-medium">{item.counted_quantity}</td>
          <td className="px-3 py-1 text-sm text-center">{item.system_quantity ?? '-'}</td>
          <td className="px-3 py-1 text-center">
            {item.match ? (
              <CheckCircleIcon className="h-4 w-4 text-green-500 inline" />
            ) : (
              <div className="flex items-center justify-center">
                <XCircleIcon className="h-4 w-4 text-red-500 inline mr-1" />
                <span className="text-xs text-red-600 font-medium">
                  {item.counted_quantity - (item.system_quantity || 0) > 0 ? '+' : ''}
                  {item.counted_quantity - (item.system_quantity || 0)}
                </span>
              </div>
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
)

export const CycleCountDetailPanel = ({ task, warehouseId, onActionSuccess = () => {}, onOpenEvent }) => {
  const { i18n } = useTranslation()
  const [expandedLocation, setExpandedLocation] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])
  const [unassignOpen, setUnassignOpen] = useState(false)
  const [reassignOpen, setReassignOpen] = useState(false)

  const locations = useMemo(() => task.locations || [], [task.locations])
  const completedCount = task.completed_locations ?? locations.filter(l => l.status === 'resolved').length
  const totalCount = task.total_locations ?? locations.length

  const toggleLocation = locationId => {
    setExpandedLocation(expandedLocation === locationId ? null : locationId)
  }

  const toggleSelected = locationId => {
    setSelectedIds(prev => (prev.includes(locationId) ? prev.filter(id => id !== locationId) : [...prev, locationId]))
  }

  const selectedLocations = useMemo(() => locations.filter(l => selectedIds.includes(l.id) && isActionableStatus(l.status)), [locations, selectedIds])

  // Group selected locations by status so the existing dialogs can detect
  // mixed status (reassign blocks) and review-pending (unassign warning).
  const syntheticBatches = useMemo(() => {
    const groups = {}
    selectedLocations.forEach(loc => {
      if (!groups[loc.status]) groups[loc.status] = []
      groups[loc.status].push(loc)
    })
    return Object.entries(groups).map(([status, locs]) => ({
      id: `${task.id}-${status}`,
      status,
      operator: task.operator,
      locations: locs,
      total_locations: locs.length,
    }))
  }, [selectedLocations, task.id, task.operator])

  const handleSuccess = type => result => {
    setUnassignOpen(false)
    setReassignOpen(false)
    setSelectedIds([])
    onActionSuccess(type, result)
  }

  const handleDownloadCsv = () => {
    const content = buildCycleCountCsv(task)
    const date = new Date().toISOString().slice(0, 10)
    downloadCsv(content, `cycle-count-${task.id}-${date}.csv`)
  }

  const getLocationStatusBadge = status => {
    const config = LOCATION_STATUS_CONFIG[status] || LOCATION_STATUS_CONFIG.pending
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>{i18n.t(`${T_PREFIX}.${config.key}`)}</span>
    )
  }

  const getOutcomeBadge = outcome => {
    if (!outcome) return null
    const config = OUTCOME_CONFIG[outcome]
    if (!config) return null
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>{i18n.t(`${T_PREFIX}.${config.key}`)}</span>
    )
  }

  return (
    <div className="p-4">
      {/* Task header */}
      <div className="mb-4 space-y-2">
        <div className="flex justify-between">
          <span className="text-sm text-gray-500">{i18n.t(`${T_PREFIX}.operator`)}</span>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{task.operator?.full_name || task.operator?.email || task.operator_name || '-'}</span>
            <button onClick={handleDownloadCsv} title={i18n.t(`${T_PREFIX}.download_csv`)} className="text-gray-400 hover:text-gray-600">
              <DownloadIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-500">{i18n.t(`${T_PREFIX}.assigned_date`)}</span>
          <span className="text-sm font-medium">{task.assigned_at ? new Date(task.assigned_at).toLocaleString() : '-'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-500">{i18n.t(`${T_PREFIX}.progress`)}</span>
          <span className="text-sm font-medium">
            {completedCount}/{totalCount} {i18n.t(`${T_PREFIX}.locations_counted`)}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className="bg-indigo-600 h-2 rounded-full" style={{ width: `${totalCount > 0 ? (completedCount / totalCount) * 100 : 0}%` }} />
        </div>
      </div>

      {/* Action bar (only when there's a selection) */}
      {selectedLocations.length > 0 && (
        <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded px-3 py-2 mb-3">
          <span className="text-sm text-indigo-700">{i18n.t(`${T_PREFIX}.selected_count`, { count: selectedLocations.length })}</span>
          <div className="flex items-center gap-2">
            <ButtonLoader type="secondary" onClick={() => setUnassignOpen(true)}>
              {i18n.t(`${T_PREFIX}.unassign_action`)}
            </ButtonLoader>
            <ButtonLoader type="primary" onClick={() => setReassignOpen(true)}>
              {i18n.t(`${T_PREFIX}.reassign_action`)}
            </ButtonLoader>
          </div>
        </div>
      )}

      {/* Locations list */}
      <div className="divide-y divide-gray-200">
        {locations.map(location => {
          const results = location.results || []
          const reviewerResults = location.reviewer_results || []
          const matches = results.filter(r => r.match).length
          const divergences = results.filter(r => !r.match).length
          const isPending = location.status === 'pending'
          const hasResults = !isPending && results.length > 0
          const isExpanded = expandedLocation === location.id
          const isCheckable = isActionableStatus(location.status)
          const isChecked = selectedIds.includes(location.id)

          return (
            <div key={location.id} className="py-2">
              {/* Location row */}
              <div
                className="flex items-center justify-between cursor-pointer hover:bg-gray-50 px-2 py-1 rounded"
                onClick={() => hasResults && toggleLocation(location.id)}
              >
                <div className="flex items-center">
                  {isCheckable ? (
                    <input
                      type="checkbox"
                      className="h-4 w-4 mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      checked={isChecked}
                      onClick={e => e.stopPropagation()}
                      onChange={() => toggleSelected(location.id)}
                    />
                  ) : (
                    <span className="w-4 mr-2" />
                  )}
                  {hasResults ? (
                    isExpanded ? (
                      <ChevronDownIcon className="h-4 w-4 text-gray-400 mr-2" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4 text-gray-400 mr-2" />
                    )
                  ) : (
                    <span className="w-4 mr-2" />
                  )}
                  <span className="text-sm font-medium">{location.location_code || location.unique_code || '-'}</span>
                </div>
                <div className="flex items-center gap-2">
                  {!isPending && (
                    <>
                      <span className="text-xs text-green-600">
                        {matches} {i18n.t(`${T_PREFIX}.matches`)}
                      </span>
                      {divergences > 0 && (
                        <span className="text-xs text-red-600 font-medium">
                          {divergences} {i18n.t(`${T_PREFIX}.divergences`)}
                        </span>
                      )}
                    </>
                  )}
                  {getLocationStatusBadge(location.status)}
                  {location.coverage_event_id != null && onOpenEvent && (
                    <button
                      type="button"
                      className="ml-2 text-xs text-indigo-700 underline hover:text-indigo-900"
                      onClick={e => {
                        e.stopPropagation()
                        onOpenEvent(location.coverage_event_id)
                      }}
                    >
                      {i18n.t('cycle_counts.events.detail.open_event', { id: location.coverage_event_id })}
                    </button>
                  )}
                  {locationHasSurplus(location) && (
                    <Link
                      className="ml-2 text-xs text-indigo-700 underline hover:text-indigo-900"
                      to={foundStockDeepLink(warehouseId, { location_code: location.location_code, cycle_count_id: location.id })}
                      onClick={e => e.stopPropagation()}
                    >
                      {i18n.t('cycle_counts.found_stock.surplus_link')}
                    </Link>
                  )}
                </div>
              </div>

              {/* Reviewer + outcome info line */}
              {!isPending && (location.reviewer || location.review_outcome) && (
                <div className="flex items-center gap-3 ml-6 mt-1 text-xs text-gray-500">
                  {location.reviewer && (
                    <span>
                      {i18n.t(`${T_PREFIX}.reviewer`)}: {location.reviewer.full_name}
                    </span>
                  )}
                  {getOutcomeBadge(location.review_outcome)}
                </div>
              )}

              {/* Expanded: per-product comparison */}
              {isExpanded && hasResults && (
                <div className="ml-6 mt-2 mb-2 space-y-3">
                  {/* Counter results */}
                  <ResultsTable results={results} i18n={i18n} countedLabel={i18n.t(`${T_PREFIX}.counted`)} />

                  {/* Reviewer results (if review has been submitted) */}
                  {reviewerResults.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase mb-1">{i18n.t(`${T_PREFIX}.reviewer_counted`)}</p>
                      <ResultsTable results={reviewerResults} i18n={i18n} countedLabel={i18n.t(`${T_PREFIX}.reviewer_counted`)} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {locations.length === 0 && <p className="text-sm text-gray-400 text-center py-4">{i18n.t(`${T_PREFIX}.no_locations`)}</p>}

      <UnassignCycleCountDialog
        open={unassignOpen}
        warehouseId={warehouseId}
        selectedBatches={syntheticBatches}
        onCancel={() => setUnassignOpen(false)}
        onSuccess={handleSuccess('unassign')}
      />

      <ReassignCycleCountDialog
        open={reassignOpen}
        warehouseId={warehouseId}
        selectedBatches={syntheticBatches}
        onCancel={() => setReassignOpen(false)}
        onSuccess={handleSuccess('reassign')}
      />
    </div>
  )
}
